import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { TCPADisclosure } from './TCPADisclosure';
import { areaFromProgram, clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
const startDateOptions = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
interface HomeInlineLeadCaptureProps {
  /** Program context from the surrounding page, sent with every API call. */
  degreeInterest?: string;
  areaStudy?: string;
  mProgram?: string;
  title?: string;
  subtitle?: string;
}
export function HomeInlineLeadCapture({
  degreeInterest = '',
  areaStudy = '',
  mProgram = '',
  title = 'Complete Your Profile',
  subtitle = 'Get matched with the best programs'
}: HomeInlineLeadCaptureProps) {
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();

  // Mounts LeadiD and TrustedForm so their hidden inputs are populated before
  // any Media Matchers call fires.
  useLeadTracking();
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  const buildProfile = useCallback((): SearchProfile => ({
    degreeInterest,
    areaStudy: areaStudy || areaFromProgram(mProgram),
    mProgram,
    currentEducation: formData.currentEducation,
    gradYear: formData.gradYear,
    startTimeframe: formData.startDate,
    militaryAffiliation: formData.isMilitary,
    zipCode: formData.zipCode.replace(/\D/g, ''),
    address: formData.address,
    city: formData.city,
    state: formData.state
  }), [degreeInterest, areaStudy, mProgram, formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);

  // Safety net for a step reached without the searches having run.
  useEffect(() => {
    if (formStep !== 3 || searchFired.current) return;
    searchFired.current = true;
    runSearches();
  }, [formStep, runSearches]);
  const retrySearches = () => {
    searchFired.current = true;
    runSearches();
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (!validateUniStep2()) return;
    lookupZip(formData.zipCode);
    // Location is the last input the payload needs, so both Searches fire
    // here — before the PII step renders — and the disclaimer is in hand when
    // the consent checkbox appears.
    if (!searchFired.current) {
      searchFired.current = true;
      runSearches();
    }
    setFormStep(3);
  };
  const handleStep3Submit = async () => {
    setSubmitError('');
    if (!validateUniStep3()) return;
    // Without the TCPA record there is no consent to post against.
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    await tcpaPost(tcpa.mRecordID, contact);
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const totalSteps = 3;
  const progressPercent = Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const renderFormContent = () => {
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-494" data-mp="1ilpusnwt281t">
          <div data-id="element-495" data-mp="1imr7ewtgz2ln">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-496" data-mp="1wjwacnxvzlco">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-497" data-mp="37y9ubhpirgnf">
              <option value="" data-id="element-498" data-mp="tsmr14fmuqv6">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-499" data-mp="ft3vrc7x7a0a">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-500" data-mp="18g7ux0kc0u45">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-501" data-mp="1ck5t991u20w5">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-502" data-mp="1cjw4tpwfs1sk">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-503" data-mp="1resfsavmr39c">
              <option value="" data-id="element-504" data-mp="3sarpgb71lvqh">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-505" data-mp="3nh5ohga8rgai">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-506" data-mp="3dlc0n0r2o0df">{errors.startDate}</p>}
          </div>

          <div data-id="element-507" data-mp="f1c76z8360se">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-508" data-mp="f1vpih1ner6y">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-509" data-mp="56bv5z2qg0kb">
              <option value="" data-id="element-510" data-mp="2lsv5p9zvw596">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-511" data-mp="11bvhqstrqemz">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-512" data-mp="1abqwt8qg2tsa">{errors.gradYear}</p>}
          </div>

          <div className="pt-2" data-id="element-513" data-mp="1f278anapg5c0">
            <button type="button" onClick={handleStep1Next} className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-514" data-mp="1f3im7fxv6tgu">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-515" data-mp="1y0tay6lve4i1">
          <div data-id="element-516" data-mp="3utmq0k64iggg">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-517" data-mp="3utv6sn5da5wx">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-518" data-mp="282z2k04lvs2d" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-519" data-mp="2vytmzix0bt94">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-520" data-mp="30t7nfzqj0qkn">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-521" data-mp="35pobyv1n16y9" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-522" data-mp="3er5o62bxcoho">
                <div data-id="element-523" data-mp="13427gw30w3si">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-524" data-mp="134bxgjo2iay7">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-525" data-mp="u4ym1lgdvcau" />
                </div>
                <div data-id="element-526" data-mp="1835bcd79o3yw">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-527" data-mp="181lxet93250a">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-528" data-mp="20p7xhrj3qrie" />
                </div>
              </div>
              <div data-id="element-529" data-mp="3ba4b4fk7pd6k">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-530" data-mp="3b9m89031o0po">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-531" data-mp="32cby5i1yqhmc">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-532" data-mp="10ius2ithdotp" />
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-533" data-mp="1ogkbuxc52z8n">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-534" data-mp="1jnhe0d0bx3mn">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-535" data-mp="27dtwght7airh" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-536" data-mp="23b6z61drg5mw">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-537" data-mp="fd67io1tog29">
          <div className="grid grid-cols-2 gap-3" data-id="element-538" data-mp="fc4ljoj9q95r">
            <div data-id="element-539" data-mp="jf14jgcq07a8">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-540" data-mp="jglzh72y5s8m">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-541" data-mp="3nvc57du37xvb" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-542" data-mp="346bg9pn7no3y">{errors.firstName}</p>}
            </div>
            <div data-id="element-543" data-mp="39437sijy187m">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-544" data-mp="393jzltk3hy6s">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-545" data-mp="2wmsigd9b5qbb" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-546" data-mp="2dw193bpblzwx">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-547" data-mp="28xq0f4arhvga">
            <div data-id="element-548" data-mp="28ubu5kyz923k">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-549" data-mp="23xv9x1uwf7vr">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-550" data-mp="1uwncsn44cd39" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-551" data-mp="1bd7ys2dpncdk">{errors.email}</p>}
            </div>
            <div data-id="element-552" data-mp="1gcaw10ng8eaz">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-553" data-mp="23zssh9hns0ka">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-554" data-mp="1uahlbsec15z1" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-555" data-mp="g9cs1rykwm58">{errors.phone}</p>}
            </div>
          </div>

          {/* Consent language comes from the TCPA Search response, so the text
              shown is the text the certificate records. */}
          {searchLoading ? <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-556" data-mp="l25w0dm83qg9">
              <LoaderIcon className="w-3.5 h-3.5 animate-spin" data-id="element-557" data-mp="3tgmyr5thh8fx" />
              Loading consent details...
            </div> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-558" data-mp="3ojo3gjy32vg5">
              <p className="text-xs text-red-800 font-medium" data-id="element-559" data-mp="6czz4wauycbj">
                We couldn't load the consent details.
              </p>
              <button type="button" onClick={retrySearches} className="mt-2 inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-red-300 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-560" data-mp="1f8967trx09z">
                Retry
              </button>
            </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" textClassName="text-xs text-gray-600 leading-relaxed cursor-pointer" data-id="element-561" data-mp="21tzw6nqu06om">
              {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
            __html: sanitizeTcpaHtml(tcpa.disclosure)
          }} data-id="element-562" data-mp="2pjc5dnm5seqx" /> : `By submitting this form, you are giving your express written consent to be contacted regarding our services using email, telephone or text, including our use of automated technology for calls or texts to the number provided above.`}
            </TCPADisclosure>}

          {submitError && <p className="text-xs text-red-600" data-id="element-563" data-mp="mw5e4l7h0wxv">{submitError}</p>}

          <div className="flex gap-3 pt-2" data-id="element-564" data-mp="hx16w043x9a6">
            <button type="button" onClick={() => setFormStep(2)} disabled={isSubmitting} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors" data-id="element-565" data-mp="hxu6mh4neyit">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-566" data-mp="11la2kk4uzxef" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-567" data-mp="wm74jjnaqrfy">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-568" data-mp="2cwfsc3n02bsw" />}
              {isSubmitting ? 'Submitting...' : 'Get My Results'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 sticky top-6 ring-1 ring-gray-900/5" data-id="element-569" data-mp="23ydx64py6ddj">
      <div className="px-6 py-4 bg-navy" data-id="element-570" data-mp="2wl8fauzal0nu">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-571" data-mp="2wm9sp63s5z8q">
          {title}
        </h2>
        <p className="text-gray-300 text-xs text-center mt-1" data-id="element-572" data-mp="2rkkgvo8qhhjl">{subtitle}</p>
      </div>

      <div className="px-6 pt-4" data-id="element-573" data-mp="2mm94b5r20sxo">
        <div className="flex justify-between items-center mb-1.5" data-id="element-574" data-mp="2mpesmiruzqsc">
          <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-575" data-mp="1g8jm9sp6uyvx">
            Step {formStep} of {totalSteps}
          </span>
          <span className="text-xs text-gray-500" data-id="element-576" data-mp="114720yz3noc1">{progressPercent}%</span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-577" data-mp="11244ltvmasxa">
          <motion.div className="h-full rounded-full bg-action" initial={{
          width: 0
        }} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-578" data-mp="163bdjw9hxy5j" />
        </div>
      </div>

      <div className="p-6" data-id="element-579" data-mp="1jtn2xnep5isz">
        <AnimatePresence mode="wait" data-id="element-580" data-mp="1jwj2tha3uhk2">
          <motion.div key={formStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-581" data-mp="1ot0134b0qcav">
            {renderFormContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}